import { Simulation } from '@/hooks/useSimulation';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { IOFAmount } from '@/utility/IOFAmount';
import { GenericAction } from '@/utility/reducers';

export interface DepositFlowStateBase {
  readonly selectedFundingMethod: PaymentRailType;
  readonly fundingMethods: readonly PaymentRailType[];
  readonly fromAmount: number | null /* null means: unset */;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number | null;
  readonly toCurrency: CurrencyCodeType;
  readonly simulationId: string | null;
}

export interface PIXDepositFlowState extends DepositFlowStateBase {
  readonly IOF?: {
    readonly label: string;
    readonly amount: IOFAmount;
  };
}

export interface SPEIDepositFlowState extends DepositFlowStateBase {
  readonly recipientName: string;
  readonly clabe: string;
  readonly accountNumber: string;
  readonly institution: string;
}

export interface ACHOrWireDepositFlowState extends DepositFlowStateBase {
  readonly beneficiaryName: string;
  readonly accountType: 'checking' | 'savings';
  readonly bankName: string;
  readonly bankAddress: string;
  readonly accountNumber: string;
  readonly routingNumber: string;
}

export interface CryptoDepositFlowState extends DepositFlowStateBase {
  readonly chain: string;
  readonly address: string;
}

export type DepositFlowState =
  | PIXDepositFlowState
  | SPEIDepositFlowState
  | ACHOrWireDepositFlowState
  | CryptoDepositFlowState;

export const initialState: DepositFlowState = {
  fundingMethods: [],
  selectedFundingMethod: PaymentRailType.unset,
  fromAmount: null,
  fromCurrency: CurrencyCodeType.UNSET,
  toAmount: null,
  toCurrency: CurrencyCodeType.UNSET,
  simulationId: null,
};

export enum DepositFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  simulationCreated = 'simulationCreated',
  depositFlowFinished = 'depositFlowFinished',
  fundingMethodChanged = 'fundingMethodChanged',
}

export type FundingMethodChangedAction = GenericAction<
  DepositFlowActionType.fundingMethodChanged,
  PaymentRailType
>;

export type TransactionFlowChangedAction = GenericAction<
  DepositFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type ToAmountChangedAction = GenericAction<
  DepositFlowActionType.toAmountChanged,
  number | null
>;

export type FromAmountChangedAction = GenericAction<
  DepositFlowActionType.fromAmountChanged,
  number | null
>;

export type SimulationCreatedAction = GenericAction<
  DepositFlowActionType.simulationCreated,
  Simulation
>;

export type ResetDepositFlowAction = GenericAction<DepositFlowActionType.depositFlowFinished, void>;

export type DepositFlowAction =
  | TransactionFlowChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | SimulationCreatedAction
  | ResetDepositFlowAction
  | FundingMethodChangedAction;

export const depositFlowReducer = (
  state: DepositFlowState,
  action: DepositFlowAction,
): DepositFlowState => {
  switch (action.type) {
    case DepositFlowActionType.transactionFlowChanged: {
      const { currency, destinations } = action.payload;

      // Failsafe only
      if (destinations.length === 0) {
        return initialState;
      }

      const destinationCurrency = destinations[0].currency;

      const fundingMethods = destinations
        .filter(
          (destination: DashboardOperationDestination): boolean =>
            destination.currency === destinationCurrency,
        )
        .map(
          (destination: DashboardOperationDestination): readonly PaymentRailType[] =>
            destination.fundingMethods ?? [],
        )
        .flat();

      return {
        ...state,
        fromCurrency: currency,
        toCurrency: destinationCurrency,
        fundingMethods,
        selectedFundingMethod:
          fundingMethods.length === 1 ? fundingMethods[0] : PaymentRailType.unset,
      };
    }
    case DepositFlowActionType.toAmountChanged: {
      const amount = action.payload;

      if (amount !== state.toAmount) {
        return {
          ...state,
          toAmount: amount,
          simulationId: null,
          fromAmount: null,
        };
      }

      return state;
    }
    case DepositFlowActionType.fromAmountChanged: {
      const amount = action.payload;

      if (amount !== state.fromAmount) {
        return {
          ...state,
          toAmount: null,
          simulationId: null,
          fromAmount: amount,
        };
      }

      return state;
    }
    case DepositFlowActionType.simulationCreated: {
      const { id, to, from } = action.payload;
      return {
        ...state,
        simulationId: id,
        fromAmount: state.fromAmount ?? from.value,
        toAmount: state.toAmount ?? to.value,
      };
    }
    case DepositFlowActionType.fundingMethodChanged:
      return { ...state, selectedFundingMethod: action.payload };
    case DepositFlowActionType.depositFlowFinished: {
      return {
        ...state,
        selectedFundingMethod: PaymentRailType.unset,
        fromAmount: null,
        fromCurrency: CurrencyCodeType.UNSET,
        toAmount: null,
        toCurrency: CurrencyCodeType.UNSET,
        simulationId: null,
      };
    }

    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: TransactionFlowChangedAction['payload'],
): TransactionFlowChangedAction => ({
  type: DepositFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const fromAmountChanged = (
  payload: FromAmountChangedAction['payload'],
): FromAmountChangedAction => ({
  type: DepositFlowActionType.fromAmountChanged,
  payload: payload,
});

export const toAmountChanged = (
  payload: ToAmountChangedAction['payload'],
): ToAmountChangedAction => ({
  type: DepositFlowActionType.toAmountChanged,
  payload: payload,
});

export const simulationCreated = (
  payload: SimulationCreatedAction['payload'],
): SimulationCreatedAction => ({
  type: DepositFlowActionType.simulationCreated,
  payload: payload,
});

export const depositFlowFinished = (): ResetDepositFlowAction => ({
  type: DepositFlowActionType.depositFlowFinished,
  payload: undefined,
});

export const fundingMethodChanged = (
  fundingMethod: PaymentRailType,
): FundingMethodChangedAction => ({
  type: DepositFlowActionType.fundingMethodChanged,
  payload: fundingMethod,
});
